<script setup lang="ts">
import { useAuth } from './composables/useAuth'
import { useLocation } from './composables/useLocation'
import { useLocationTracking, updateGlobalAuthState } from './composables/useLocationTracking'
import { onMounted, ref, watch, computed, onUnmounted } from 'vue'
import AppLayout from './layouts/AppLayout.vue'
import LocationPermission from './components/LocationPermission.vue'
import ToastContainer from './components/ToastContainer.vue'
import { useRoute, useRouter } from 'vue-router'
import api from './api/axios'
import { syncOfflineActions, cacheLocationsForUser, getLocationsFromCache } from './services/offlineSync'

function handleOnline() {
  syncOfflineActions(api).then(() => {
    // Optional: Toast/Alert anzeigen
    // alert('Offline-Aktionen wurden synchronisiert.')
    // TODO: Schöner Toast
    console.log('Offline-Aktionen synchronisiert.')
  })
}

window.addEventListener('online', handleOnline)

const { state, initializeAuth, isTokenExpiringSoon, refreshToken, isTokenExpired } = useAuth()
const {
  startLocationTracking,
  loadLocationPermission,
  verifyLocationPermission,
  isLocationBlocked,
  watchLocationPermission,
  stopWatchingLocationPermission
} = useLocation()
const locationTracking = useLocationTracking()
const route = useRoute()
const router = useRouter()
const transitionName = ref('fade')

// Standort-Einwilligung Status
const showLocationPermission = ref(false)
const locationPermissionGranted = ref(false)
const isInitializing = ref(true)
const locationPermissionWatcher = ref<NodeJS.Timeout | null>(null)

// Retry logic variables
let locationFailureCount = 0
const MAX_LOCATION_FAILURES = 5 // Allow 5 consecutive failures before redirect

// Funktion zur Überwachung des Standortzugriffs
const startLocationPermissionWatcher = () => {
  // Stoppe vorherigen Watcher falls vorhanden
  if (locationPermissionWatcher.value) {
    clearInterval(locationPermissionWatcher.value)
  }
  
  // Starte neuen Watcher
  locationPermissionWatcher.value = setInterval(async () => {
    if (!navigator.onLine) {
      // Offline: Standortprüfung überspringen
      return
    }
    try {
      // Prüfe, ob der Benutzer nicht auf der LocationRequiredView ist
      if (route.name === 'location-required') {
        // Stoppe Watcher wenn auf LocationRequiredView
        stopLocationPermissionWatcher()
        return
      }
      
      // Prüfe, ob der Benutzer eingeloggt ist
      if (!state.value.isAuthenticated || !state.value.user) {
        return
      }
      
      // Frage das GERÄT, nicht den localStorage. Der gespeicherte Wert war
      // 30 Tage lang die einzige Wahrheit — dadurch lief die App fröhlich
      // weiter, wenn die Ortung am Gerät entzogen wurde, und sperrte umgekehrt
      // am 31. Tag Fahrer aus, die die Ortung längst erlaubt hatten.
      const verdict = await verifyLocationPermission()

      console.log('Location permission verdict:', { verdict, routeName: route.name })

      if (verdict === 'granted') {
        locationFailureCount = 0
        locationPermissionGranted.value = true
        return
      }

      if (verdict === 'unknown') {
        // Kein Signal / Timeout — das ist KEINE Ablehnung. Weiterarbeiten.
        console.log('Location temporarily unavailable, permission unchanged')
        return
      }

      // Ausdrücklich verweigert: eindeutig, also sofort reagieren statt fünf
      // Runden (2,5 Minuten) zu warten wie bisher.
      console.log('Location permission denied by device, redirecting')
      locationFailureCount = 0
      locationPermissionGranted.value = false
      locationTracking.stopBackgroundTracking()
      router.push('/location-required')
    } catch (error) {
      console.error('Error checking location permission:', error)
      locationFailureCount++
      console.log(`Location check error (${locationFailureCount}/${MAX_LOCATION_FAILURES})`)
      
      // Only redirect after MAX_LOCATION_FAILURES consecutive failures
      if (locationFailureCount >= MAX_LOCATION_FAILURES) {
        console.log('Max location errors reached, redirecting to location-required')
        locationFailureCount = 0
        
        // Bei Fehlern auch zur LocationRequiredView weiterleiten
        locationPermissionGranted.value = false
        locationTracking.stopBackgroundTracking()
        router.push('/location-required')
      } else {
        console.log('Location check error but within retry threshold, continuing...')
      }
    }
  }, 30000) // Prüfe alle 30 Sekunden (reduzierte Frequenz für bessere Performance)
}

// Stoppe die Standortzugriff-Überwachung
const stopLocationPermissionWatcher = () => {
  if (locationPermissionWatcher.value) {
    clearInterval(locationPermissionWatcher.value)
    locationPermissionWatcher.value = null
  }
}

// Beobachte den Auth-Status
watch(() => state.value.user, async (newUser, oldUser) => {
  console.log('[DEBUG] Auth state changed:', { newUser, oldUser, isAuthenticated: state.value.isAuthenticated })
  
  // Wenn sich der Auth-Status geändert hat
  if (newUser !== oldUser) {
    // Wenn der Benutzer ausgeloggt wird
    if (!newUser) {
      console.log('[DEBUG] User logged out, navigating to login')
      stopLocationPermissionWatcher()
      router.push('/login')
    } else {
      // Direkt nach Login: Standorte cachen, falls online
      if (newUser && newUser.airport_id && navigator.onLine) {
        console.log('[DEBUG] Versuche Standorte zu cachen für airport_id:', newUser.airport_id)
        await cacheLocationsForUser(newUser, api)
      } else {
        console.log('[DEBUG] Kein airport_id oder offline, cachen übersprungen', newUser)
      }
      if (newUser && locationPermissionGranted.value) {
      // Wenn der Benutzer eingeloggt wird und Standort-Einwilligung vorhanden ist
        console.log('[DEBUG] User logged in, starting background location tracking')
      await locationTracking.startBackgroundTracking()
      startLocationPermissionWatcher()
      }
    }
  }
  if (newUser && newUser.airport_id) {
    if (navigator.onLine) {
      console.log('[DEBUG] (zweiter Block) Versuche Standorte zu cachen für airport_id:', newUser.airport_id)
      await cacheLocationsForUser(newUser, api)
    } else {
      // Offline: Prüfe, ob Standorte im Cache sind, und logge das Ergebnis
      const cached = await getLocationsFromCache(newUser.airport_id)
      if (cached && Array.isArray(cached)) {
        console.log('[DEBUG] OFFLINE: Standorte im globalen Cache gefunden:', cached)
      } else {
        console.warn('[DEBUG] OFFLINE: Keine Standorte im globalen Cache gefunden')
      }
    }
  }
}, { immediate: true })

// Beobachte Routenänderungen für dynamische Übergänge
watch(() => route.meta.transition, (newTransition) => {
  if (newTransition) {
    transitionName.value = newTransition as string
  } else {
    // Standardübergang basierend auf Navigationsrichtung
    const depth = route.meta.depth || 0
    const prevDepth = route.meta.prevDepth || 0
    
    if (depth > prevDepth) {
      transitionName.value = 'slide-up'
    } else if (depth < prevDepth) {
      transitionName.value = 'slide-down'
    } else {
      transitionName.value = 'slide-left'
    }
  }
})

// Beobachte localStorage-Änderungen für Standort-Einwilligung
watch(() => route.name, async (newRouteName) => {
  // Wenn wir auf der LocationRequiredView sind, prüfe regelmäßig localStorage
  if (newRouteName === 'location-required') {
    const checkInterval = setInterval(async () => {
      const savedPermission = loadLocationPermission()
      if (savedPermission && !locationPermissionGranted.value) {
        console.log('Standort-Einwilligung in localStorage gefunden, initialisiere App')
        clearInterval(checkInterval)
        
        locationPermissionGranted.value = true
        isInitializing.value = false
        
        // Initialisiere Auth und starte Tracking
        await startLocationTracking()
        await initializeAuth()
        updateGlobalAuthState(state.value)
        
        // Prüfe Auth-Status und navigiere entsprechend
        if (!state.value.isAuthenticated || !state.value.user) {
          console.log('User not authenticated, navigating to login')
          router.push('/login')
        } else {
          console.log('User is authenticated, navigating to dashboard')
          router.push('/dashboard')
        }
      }
    }, 1000) // Prüfe jede Sekunde
    
    // Cleanup nach 30 Sekunden
    setTimeout(() => clearInterval(checkInterval), 30000)
  }
})

// Standort-Einwilligung Handler
const handleLocationPermissionGranted = async () => {
  console.log('Standortzugriff gewährt - handleLocationPermissionGranted aufgerufen')
  locationPermissionGranted.value = true
  showLocationPermission.value = false
  
  // Starte dauerhaftes Tracking
  await startLocationTracking()
  
  // Initialisiere Auth und setze isInitializing auf false
  await initializeAuth()
  updateGlobalAuthState(state.value)
  isInitializing.value = false
  
  // Starte Location Tracking wenn User bereits eingeloggt ist
  if (state.value.isAuthenticated && state.value.user) {
    console.log('User already authenticated, starting background location tracking')
    await locationTracking.startBackgroundTracking()
  }
  
  // Starte Location Permission Watcher
  startLocationPermissionWatcher()
  
  // Navigiere basierend auf Auth-Status
  if (!state.value.isAuthenticated || !state.value.user) {
    console.log('User not authenticated, navigating to login')
    router.push('/login')
  } else {
    console.log('User is authenticated, navigating to dashboard')
    router.push('/dashboard')
  }
}

const handleLocationPermissionDenied = () => {
  console.log('Standortzugriff verweigert - handleLocationPermissionDenied aufgerufen')
  locationPermissionGranted.value = false
  showLocationPermission.value = false
  isInitializing.value = false
  
  // Navigiere zur LocationRequiredView
  router.push('/location-required')
}

// Prüfe Standort-Einwilligung beim Start
const checkLocationPermissionOnStart = async () => {
  console.log('checkLocationPermissionOnStart aufgerufen')
  
  // Prüfe zuerst localStorage
  const savedPermission = loadLocationPermission()
  console.log('Gespeicherte Einwilligung:', savedPermission)
  
  if (savedPermission) {
    console.log('Gespeicherte Standort-Einwilligung gefunden')
    locationPermissionGranted.value = true

    // Cache akzeptieren, damit der Kaltstart schnell bleibt — aber im
    // Hintergrund am Gerät gegenprüfen. Nur eine ausdrückliche Ablehnung
    // schickt den Fahrer auf die "Standort erforderlich"-Seite.
    verifyLocationPermission().then((verdict) => {
      if (verdict === 'denied') {
        console.log('Cached consent is stale, device denies location')
        locationPermissionGranted.value = false
        locationTracking.stopBackgroundTracking()
        router.push('/location-required')
      }
    }).catch((error) => {
      console.warn('Background location verification failed:', error)
    })

    // Initialisiere Auth und starte Tracking parallel
    const authPromise = initializeAuth()
    const locationPromise = startLocationTracking()
    
    // Warte auf beide Promises (allSettled damit Auth nicht von Location-Fehler blockiert wird)
    const [authResult, locationResult] = await Promise.allSettled([authPromise, locationPromise])
    if (locationResult.status === 'rejected') {
      console.warn('Location tracking initialization failed:', locationResult.reason)
    }
    if (authResult.status === 'rejected') {
      console.error('Auth initialization failed:', authResult.reason)
    }
    
    // Setze globalen Auth-Status für Tracking
    updateGlobalAuthState(state.value)
    // Setze isInitializing auf false, nachdem Auth initialisiert ist
    isInitializing.value = false
    
    // Starte Location Tracking wenn User bereits eingeloggt ist
    if (state.value.isAuthenticated && state.value.user) {
      console.log('User already authenticated, starting background location tracking')
      await locationTracking.startBackgroundTracking()
    }
    
    // Starte Location Permission Watcher wenn User eingeloggt ist
    if (state.value.isAuthenticated && state.value.user) {
      startLocationPermissionWatcher()
    }
    
    // Navigiere basierend auf Auth-Status
    if (!state.value.isAuthenticated || !state.value.user) {
      console.log('User not authenticated, navigating to login')
      router.push('/login')
    } else {
      console.log('User is authenticated, navigating to dashboard')
      router.push('/dashboard')
    }
    return
  }
  
  // Keine gespeicherte Einwilligung - prüfe tatsächlichen Standortzugriff
  console.log('Keine gespeicherte Einwilligung, prüfe tatsächlichen Standortzugriff')
  
  if (!navigator.onLine) {
    // Offline: Standortprüfung überspringen
    locationPermissionGranted.value = true
    isInitializing.value = false
    await initializeAuth()
    updateGlobalAuthState(state.value)
    await startLocationTracking()
    if (!state.value.isAuthenticated || !state.value.user) {
      router.push('/login')
    } else {
      router.push('/dashboard')
    }
    return
  }
  
  try {
    // Teste Standortzugriff mit strikterer Prüfung
    const locationTest = new Promise<boolean>((resolve) => {
      const timeout = setTimeout(() => {
        console.log('Initial location test timeout - redirecting to location-required')
        resolve(false)
      }, 8000) // 8 Sekunden Timeout
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeout)
          console.log('Initial location test successful:', position)
          resolve(true)
        },
        (error) => {
          clearTimeout(timeout)
          console.log('Initial location test failed:', error.code, error.message)
          resolve(false)
        },
        {
          enableHighAccuracy: false,
          timeout: 6000,
          maximumAge: 0
        }
      )
    })
    
    const locationWorks = await locationTest
    
    if (locationWorks) {
      console.log('Standortzugriff funktioniert, speichere Einwilligung')
      locationPermissionGranted.value = true
      
      // Speichere die Einwilligung
      localStorage.setItem('ragacars_location_permission', 'granted')
      localStorage.setItem('ragacars_location_permission_timestamp', Date.now().toString())
      
      // Initialisiere Auth und starte Tracking
      const authPromise = initializeAuth()
      const locationPromise = startLocationTracking()
      const [authRes, locRes] = await Promise.allSettled([authPromise, locationPromise])
      if (locRes.status === 'rejected') {
        console.warn('Location tracking initialization failed:', locRes.reason)
      }
      if (authRes.status === 'rejected') {
        console.error('Auth initialization failed:', authRes.reason)
      }
      updateGlobalAuthState(state.value)
      isInitializing.value = false
      
      // Starte Location Tracking wenn User bereits eingeloggt ist
      if (state.value.isAuthenticated && state.value.user) {
        await locationTracking.startBackgroundTracking()
        startLocationPermissionWatcher()
        router.push('/dashboard')
      } else {
        router.push('/login')
      }
      return
    } else {
      console.log('Standortzugriff nicht verfügbar, navigiere zur LocationRequiredView')
      isInitializing.value = false
      locationPermissionGranted.value = false
      router.push('/location-required')
    }
  } catch (error) {
    console.error('Fehler beim Prüfen des Standortzugriffs:', error)
    isInitializing.value = false
    locationPermissionGranted.value = false
    router.push('/location-required')
  }
}

onMounted(async () => {
  // Setze einen Timeout für die Initialisierung
  const initTimeout = setTimeout(() => {
    console.log('Initialization timeout reached, forcing layout display')
    isInitializing.value = false
  }, 3000) // 3 Sekunden Timeout
  
  try {
    // Prüfe zuerst die Standort-Einwilligung
    await checkLocationPermissionOnStart()
    
    // Update globalen Auth-Status für Location-Tracking
    updateGlobalAuthState(state.value)
    
    // Starte Location Tracking wenn User bereits eingeloggt ist
    if (state.value.isAuthenticated && state.value.user && locationPermissionGranted.value) {
      console.log('App mounted: User already authenticated, starting location tracking')
      
      // Warte einen kurzen Moment, um sicherzustellen, dass der Auth-Status vollständig geladen ist
      await new Promise(resolve => setTimeout(resolve, 200))
      
      // Update global auth state and start tracking
      locationTracking.startBackgroundTrackingIfNotRunning()
      
      // Starte Location Permission Watcher
      startLocationPermissionWatcher()
    }
  } catch (error) {
    console.error('Error during initialization:', error)
    // Bei Fehlern trotzdem das Layout anzeigen
    isInitializing.value = false
  } finally {
    // Clear timeout und setze isInitializing auf false
    clearTimeout(initTimeout)
    isInitializing.value = false
  }
})

onUnmounted(() => {
  stopLocationPermissionWatcher()
})

// Computed property für die Layout-Bedingung
const shouldShowLayout = computed(() => {
  const isAuthenticated = state.value.isAuthenticated && !!state.value.user
  const hasToken = !!localStorage.getItem('token')
  const isNotLoginPage = route.name !== 'login'
  const isNotLocationRequiredPage = route.name !== 'location-required'
  const isNotDriverVerificationPage = route.name !== 'driver-verification'
  const hasLocationPermission = locationPermissionGranted.value
  const isNotInitializing = !isInitializing.value
  
  console.log('Layout condition:', { 
    isAuthenticated, 
    hasToken,
    isNotLoginPage, 
    isNotLocationRequiredPage,
    isNotDriverVerificationPage,
    hasLocationPermission,
    isNotInitializing,
    user: state.value.user,
    authState: state.value.isAuthenticated,
    routeName: route.name
  })
  
  // Zeige Layout wenn:
  // 1. Benutzer authentifiziert ist und nicht auf Login/LocationRequired/DriverVerification-Seiten
  // 2. ODER wenn Token vorhanden ist (für Reload-Szenarien) und nicht auf Login/LocationRequired/DriverVerification-Seiten
  return (isAuthenticated || hasToken) && isNotLoginPage && isNotLocationRequiredPage && isNotDriverVerificationPage
})

// Computed property für die Router-View-Bedingung
const shouldShowRouterView = computed(() => {
  const isLocationRequiredPage = route.name === 'location-required'
  const hasLocationPermission = locationPermissionGranted.value
  const isNotInitializing = !isInitializing.value
  
  console.log('Router view condition:', { 
    isLocationRequiredPage,
    hasLocationPermission,
    isNotInitializing,
    routeName: route.name
  })
  
  // Zeige Router-View für LocationRequired ODER wenn Standortzugriff vorhanden ist
  return isLocationRequiredPage || (hasLocationPermission && isNotInitializing)
})

// Computed property für die Standort-Dialog-Bedingung
const shouldShowLocationDialog = computed(() => {
  const hasLocationPermission = locationPermissionGranted.value
  const isNotInitializing = !isInitializing.value
  
  console.log('Location dialog condition:', { 
    hasLocationPermission,
    isNotInitializing,
    showLocationPermission: showLocationPermission.value
  })
  
  // Zeige Dialog nur wenn explizit gesetzt
  return showLocationPermission.value
})

// Transition-Hooks — call done() immediately to prevent white-screen flicker.
// Delayed done() with opacity:0 caused components to stay invisible on mobile devices
// while the main thread was busy (e.g. processing damage categories).
const beforeEnter = (el: Element) => {
  (el as HTMLElement).style.opacity = '0'
}

const enter = (el: Element, done: () => void) => {
  requestAnimationFrame(() => {
    (el as HTMLElement).style.opacity = '1'
    done()
  })
}

const leave = (el: Element, done: () => void) => {
  requestAnimationFrame(() => {
    (el as HTMLElement).style.opacity = '0'
    done()
  })
}

const isOnline = ref(navigator.onLine)

function updateOnlineStatus() {
  isOnline.value = navigator.onLine
}

// App resume handler - checks both GPS and token when app comes to foreground
const handleVisibilityChange = async () => {
  if (document.visibilityState !== 'visible') {
    return
  }

  console.log('[App] App resumed, checking GPS and token...')

  // Skip if not authenticated or on special pages
  if (!state.value.isAuthenticated || !state.value.user) {
    console.log('[App] Not authenticated, skipping resume checks')
    return
  }

  if (route.name === 'login' || route.name === 'location-required') {
    console.log('[App] On auth page, skipping resume checks')
    return
  }

  // Skip if offline
  if (!navigator.onLine) {
    console.log('[App] Offline, skipping resume checks')
    return
  }

  // 1. Check GPS permission against the device (not just the cached consent).
  // A temporary failure (no signal, timeout) never redirects — only an explicit
  // denial does.
  try {
    if (await isLocationBlocked()) {
      console.log('[App] Location denied on resume, redirecting')
      locationPermissionGranted.value = false
      locationTracking.stopBackgroundTracking()
      router.push('/location-required')
      return
    }
    locationPermissionGranted.value = true
  } catch (e) {
    console.warn('[App] Location check error on resume:', e)
    // Don't redirect on check errors, let the regular monitoring handle it
  }

  // 2. Check token expiration
  if (isTokenExpired()) {
    console.log('[App] Token expired on resume, attempting refresh...')
    const refreshed = await refreshToken()
    if (!refreshed) {
      console.log('[App] Token refresh failed on resume, redirecting to login')
      router.push('/login')
      return
    }
    console.log('[App] Token refreshed successfully on resume')
  } else if (isTokenExpiringSoon(30)) {
    // Token expires within 30 minutes, proactively refresh
    console.log('[App] Token expiring soon on resume, refreshing proactively...')
    refreshToken() // Don't await - background refresh
  }

  // 3. Revive location tracking. The timer is frozen (and often killed) while
  // the PWA is in the background; without this the recording stayed dead until
  // a full reload or re-login. Done last so the first position after waking up
  // goes out with a fresh token. Idempotent — does nothing if tracking is fine.
  await locationTracking.ensureTrackingHealthy()

  console.log('[App] Resume checks passed')
}

onMounted(() => {
  window.addEventListener('online', updateOnlineStatus)
  window.addEventListener('offline', updateOnlineStatus)
  document.addEventListener('visibilitychange', handleVisibilityChange)

  // React the moment the device permission flips, instead of waiting for the
  // next 30s poll. Silently does nothing on browsers without the Permissions API.
  watchLocationPermission(async (permissionState) => {
    if (permissionState === 'denied') {
      console.log('[App] Location permission revoked on the device')
      locationPermissionGranted.value = false
      locationTracking.stopBackgroundTracking()
      if (route.name !== 'location-required') {
        router.push('/location-required')
      }
      return
    }

    if (permissionState === 'granted') {
      console.log('[App] Location permission granted on the device')
      locationPermissionGranted.value = true
      await locationTracking.ensureTrackingHealthy()
    }
  })
})

onUnmounted(() => {
  window.removeEventListener('online', updateOnlineStatus)
  window.removeEventListener('offline', updateOnlineStatus)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  stopWatchingLocationPermission()
})
</script>

<template>
  <div>
    <!-- Offline-Banner -->
    <div v-if="!isOnline" style="position: fixed; top: 12px; right: 12px; z-index: 9999;">
      <span class="px-3 py-1 rounded bg-red-600 text-white shadow">OFFLINE</span>
    </div>
  <!-- Standort-Einwilligung Dialog -->
  <LocationPermission 
    v-if="shouldShowLocationDialog"
    @permission-granted="handleLocationPermissionGranted"
    @permission-denied="handleLocationPermissionDenied"
  />
  
  <!-- Loading Screen während der Initialisierung -->
  <div v-else-if="isInitializing" class="fixed inset-0 bg-gray-100 flex items-center justify-center z-40">
    <div class="text-center">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E9041D] mx-auto mb-4"></div>
      <p class="text-gray-600">Initialisiere App...</p>
    </div>
  </div>
  
  <!-- Wenn der Benutzer eingeloggt ist und nicht auf der Login-Seite, verwende das AppLayout -->
  <AppLayout v-else-if="shouldShowLayout">
    <!-- Nur der Seiteninhalt wird animiert -->
    <router-view v-slot="{ Component }">
      <transition
        :name="transitionName"
        mode="out-in"
        :css="false"
        @before-enter="beforeEnter"
        @enter="enter"
        @leave="leave"
      >
        <component :is="Component" :key="$route.fullPath" />
      </transition>
    </router-view>
  </AppLayout>
  <!-- Zeige Router-View für LocationRequired oder wenn Standortzugriff vorhanden ist -->
  <router-view v-else-if="shouldShowRouterView" v-slot="{ Component }">
    <component :is="Component" />
  </router-view>
  <!-- Fallback: Zeige Loading-Screen -->
  <div v-else class="min-h-screen bg-gray-100 flex items-center justify-center">
    <div class="text-center">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E9041D] mx-auto mb-4"></div>
      <p class="text-gray-600">App wird geladen...</p>
    </div>
  </div>
  
  <!-- Toast Container für Benachrichtigungen -->
  <ToastContainer />
  </div>
</template>

<style>
/* Global styles */
html {
  @apply h-full bg-gray-100;
}

body {
  @apply h-full antialiased text-gray-900;
}

#app {
  @apply h-full;
}

</style>
